import {Show, HStack, Box, useDevice} from 'platform/foundation';
import styled from 'styled-components';

import {useEffect, useState} from 'react';

import {or} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {browserStorageKey} from '@dms/config';
import {MAIN_TOPBAR_HEIGHT} from '@dms/shared';

import {ActivitySidebarProvider} from '../contexts/ActivitySidebarProvider';
import {useActivitySidebarWidth} from '../hooks/useActivitySidebarWidth';
import {useSidebarHeight} from '../hooks/useSidebarHeight';
import {ActivitySidebarAction} from '../types/ActivitySidebarAction';
import {getActivitySidebarWidthCss} from '../utils/getActivitySidebarWidthCss';
import {ActivitySidebarItemV2} from './ActivitySidebarItemV2';
import {ActivitySidebarMenu} from './ActivitySidebarMenu';
import {ActivitySidebarResizeHandle} from './ActivitySidebarResizeHandle';

const DESKTOP_BREAKPOINT = '(min-width: 1451px)';

interface ActivitySidebarV2Props {
  actions: ActivitySidebarAction[];
}

export function ActivitySidebarV2(props: ActivitySidebarV2Props) {
  const device = useDevice();
  const shouldOpenActivityPanel = localStorage.getItem(browserStorageKey.SALES_ACTIVITY_PANEL_OPEN);
  const {ref: stickyRef, height: sidebarHeight} = useSidebarHeight();
  const sidebarWidth = useActivitySidebarWidth();

  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia(DESKTOP_BREAKPOINT).matches);

  useEffect(function subscribeToDesktopBreakpoint() {
    const query = window.matchMedia(DESKTOP_BREAKPOINT);
    const listener = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    query.addEventListener('change', listener);
    return () => query.removeEventListener('change', listener);
  }, []);

  // On desktop ( with DESKTOP_BREAKPOINT ≥1451px) the panel is in-flow and pushes content left.
  // On notebook/tablet/mobile (<1451px) the panel overlays content using absolute positioning.
  const isOverlay = !isDesktop;
  const isMobileOrTablet = or(device === 'mobile', device === 'tablet');

  const [openTab, setOpenTab] = useState(() => {
    if (shouldOpenActivityPanel === 'false') {
      return null;
    }

    if (!props.actions.some((item) => item.id === shouldOpenActivityPanel)) {
      return null;
    }

    return shouldOpenActivityPanel;
  });

  const handleCloseTab = () => {
    setOpenTab(null);
    localStorage.setItem(browserStorageKey.SALES_ACTIVITY_PANEL_OPEN, 'false');
  };

  const handleToggleTab = (tabId: string) => {
    const newTab = openTab === tabId ? null : tabId;
    setOpenTab(newTab);
    localStorage.setItem(browserStorageKey.SALES_ACTIVITY_PANEL_OPEN, newTab ?? 'false');
  };

  const activeAction = props.actions.find((action) => action.id === openTab);

  if (isNilOrEmpty(props.actions)) {
    return null;
  }

  return (
    <ActivitySidebarProvider handleToggleTab={handleToggleTab}>
      {/*
       * StickyContainer uses position: sticky with top: 0.
       *
       * Why top: 0 and not the TopBar height?
       * #dms-app-container starts directly below the TopBar (it is a grid cell in row 2).
       * So top: 0 inside #dms-app-container already means "top of the area below the TopBar".
       * No additional offset is needed.
       *
       * Why align-self: flex-start?
       * Without it, the sticky element stretches to fill its flex parent's full height
       * (align-items: stretch default), making it as tall as the content and removing
       * the sticky effect. align-self: flex-start lets it be shorter than its parent
       * so sticky has something to anchor against.
       *
       * Height is computed by useSidebarHeight which measures the sidebar's actual
       * top position in the viewport and sets height = viewportHeight - sidebarTop.
       * This correctly accounts for the entity header height on initial load and
       * gives the full viewport-minus-TopBar height once the header has scrolled away.
       */}
      <StickyContainer
        ref={stickyRef}
        $isMobileOrTablet={isMobileOrTablet}
        style={{height: isMobileOrTablet ? undefined : sidebarHeight}} // eslint-disable-line react/forbid-component-props -- Pre-existing violation
      >
        <HStack spacing={0} height="100%">
          <Show when={openTab}>
            <Box
              ref={sidebarWidth.panelRef}
              position={isOverlay ? 'absolute' : 'relative'}
              zIndex={isMobileOrTablet ? 'HEADER' : isOverlay ? 'CONTENT' : undefined} // eslint-disable-line no-nested-ternary -- Pre-existing violation
              left={isMobileOrTablet ? 0 : undefined}
              right={isMobileOrTablet ? 0 : isOverlay ? 12 : undefined} // eslint-disable-line no-nested-ternary -- Pre-existing violation
              top={isMobileOrTablet ? MAIN_TOPBAR_HEIGHT : undefined}
              bottom={isMobileOrTablet ? 0 : undefined}
              width={isMobileOrTablet ? undefined : getActivitySidebarWidthCss(sidebarWidth.width)}
              height={isMobileOrTablet ? undefined : '100%'}
              backgroundColor="general.white"
              borderRight="1px solid"
              borderColor="general.separator"
              boxShadow="elevation_3"
              overflow="hidden"
            >
              <Show when={!isMobileOrTablet}>
                <ActivitySidebarResizeHandle
                  width={sidebarWidth.width}
                  min={sidebarWidth.bounds.min}
                  max={sidebarWidth.bounds.max}
                  onChange={sidebarWidth.setWidth}
                  onReset={sidebarWidth.resetWidth}
                />
              </Show>
              <ActivitySidebarItemV2
                title={activeAction?.label ?? ''}
                onClose={handleCloseTab}
                disablePadding={activeAction?.disablePadding}
                actions={props.actions}
                handleToggleTab={handleToggleTab}
                activeTab={openTab}
              >
                {activeAction?.component ?? null}
              </ActivitySidebarItemV2>
            </Box>
          </Show>
          <ActivitySidebarMenu
            actions={props.actions}
            onClick={handleToggleTab}
            activeTab={openTab}
          />
        </HStack>
      </StickyContainer>
    </ActivitySidebarProvider>
  );
}

const StickyContainer = styled.div<{$isMobileOrTablet: boolean}>`
  position: ${({$isMobileOrTablet}) => ($isMobileOrTablet ? 'static' : 'sticky')};
  top: 0;
  align-self: ${({$isMobileOrTablet}) => ($isMobileOrTablet ? 'stretch' : 'flex-start')};
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
`;
